import { ref } from 'vue'
import cookie from 'js-cookie'
import { time2Date, unzip } from '~/utils/tools'
import { percent, sub } from '~/utils/math'
import { objectOmit, set, useTimestamp } from '@vueuse/core'
import InnerWebSocket from 'ws'
import { getDomain } from '@/utils'

export const timestamp = useTimestamp()
export const overview = ref({})
export const kline = ref({})
export const orderbook = ref({})
export const trades = ref([])
export const depthChartData = ref({})
export const wallet = ref({})
export const spotHomePageTopTen24 = ref([])
export const orders = ref({
  os: [],
})
export const ordersLoading = ref(true)

export const resetTrades = () => set(trades, [])
export const resetOrderbook = () => set(orderbook, {})
export const resetKline = () => set(kline, {})

export const formatter = {
  spot_order(msg) {
    ordersLoading.value = false

    let { delOrderIds: ds = [], incrOrderList: is = [], modifyOrderList: ms = [], orderVoList: os = [], orderCancelAll } = msg ?? {}
    const fn = data => data.reduce((sofar, next) => {
      sofar[next.ordId] = next
      return sofar
    }, {})

    if (os.length > 0 || orderCancelAll) {
      orders.value.os = os
    }
    else {
      const oos = fn(orders.value.os)
      Object.assign(oos, fn(is), fn(ms))
      ds.forEach(i => delete oos[i])
      os = Object.values(oos)
    }

    orders.value = {
      ...msg,
      orderVoList: os,
      ds,
      is,
      ms,
      os,
    }
  },
  home_page_top_ten24(msg) {
    const d = JSON.parse(unzip(msg.data))
    spotHomePageTopTen24.value = d.ids
  },
  ticker(msg) {
    const d = JSON.parse(unzip(msg.data))
    const f = (o) => {
      const r = +o.open ? sub(o.close, o.open).div(o.open).toFixed() : ''
      return Object.assign(o, {
        id: o.instrumentId,
        code: o.symbol,
        lp: o.c ?? o.close,
        r,
        rt: percent(r),
        a: o.amount,
        vol: o.volume,
        c: o.c ?? o.close,
      })
    }
    if (Array.isArray(d)) {
      overview.value = d.reduce((sofar, cur) => {
        const o = f(cur)
        sofar[o.id] = Object.assign(sofar[o.id] ?? {}, o)
        return sofar
      }, overview.value)
    }
    else {
      const o = f(cur)
      overview.value[o.id] = Object.assign(overview.value[o.id] ?? {}, o)
    }
  },
  kline(msg) {
    msg.id = msg.instrumentId
    kline.value = msg
  },
  snapshot_depth(msg) {
    orderbook.value = {
      ...msg,
      asks: msg.a,
      bids: msg.b,
      code: msg.symbol,
      id: msg.instrumentId,
    }
  },
  trade(msg) {
    const fn = o => ({
      id: o.ts,
      code: o.symbol,
      tm: time2Date(o.time, 'h:i:s'),
      side: o.takerSide === 'SELL' ? -1 : 1,
      ...o,
    })
    if (Array.isArray(msg.data)) {
      trades.value = msg.data
        .map(t => fn(t))
    }
    else {
      const { ts, price, instrumentId } = msg
      if (overview.value[instrumentId]) {
        overview.value[instrumentId].c = price
        set(timestamp, ts / 1000)
      }
      const arr = [
        fn(msg),
      ]
        .concat(trades.value)
        .slice(0, 50)
      trades.value = arr
    }
  },
  depth(msg) {
    depthChartData.value = {
      ...msg,
      asks: msg.a,
      bids: msg.b,
      code: msg.symbol,
      id: msg.instrumentId,
    }
  },
  spot_asset(msg = {}) {
    wallet.value = msg.assets?.reduce((sofar, cur) => {
      cur.id = cur.currencyId
      cur.code = cur.symbol
      cur.froze = cur.frozenAmount
      cur.locked = cur.lockedAmount
      cur.trade = cur.usableAmount
      sofar[cur.id] = cur
      return sofar
    }, {})
    wallet.value._ALL = Object.assign(objectOmit(msg, ['assets', 'T', 'S']), {
      btcVal: msg.btcValuation,
      cnyVal: msg.cnyValuation,
      usdVal: msg.usdValuation,
    })
  },
}

export class WS {
  wsUrl = `wss://ws-spot.${getDomain()}/s/ws`
  socket
  /**
   * 收集器
   * topic =>  {msg, cbs}
   */
  co = {
    sub: {},
    req: {},
  }

  timerHb = null // 心跳
  timerRc = null // 重连

  constructor() {
    if (import.meta.client) {
      this.connect()
    }
  }

  connect(url = this.wsUrl) {
    if (this.socket && this.socket.readyState !== 3) {
      this.socket.close()
      this.socket = null
    }
    this.socket = new WebSocket(url)
    this.socket.onopen = this.onopen.bind(this)
    this.socket.onmessage = this.onmessage.bind(this)
    this.socket.onerror = this.onerror.bind(this)
    this.socket.onclose = this.onclose.bind(this)
  }

  onopen() {
    this.auth()
    this.reSend()
    this.resetHeartbeat()
  }

  onmessage(msg) {
    try {
      const d = JSON.parse(msg.data)
      formatter[d.T]?.(d)

      if (d.T === 'req') {
        const item = this.co.req[d.channel]
        item?.cbs?.forEach(i => i(d))
        delete this.co.req[d.channel]
      }
      else if (d.T === 'ping') {
        this.pong()
      }
      else {
        this.co.sub[d.channel]?.cbs?.forEach(i => i(d))
      }

      // 每次收到消息后，重置心跳
      this.resetHeartbeat()
    }
    catch (e) {
      console.log('ws onmessage', e)
    }
  }

  onerror(e) {
    console.log('ws error', e)
    this.timerRc && clearTimeout(this.timerRc)
    this.timerRc = setTimeout(() => this.connect(), 5000)
  }

  onclose() {
    console.log('ws close')
  }

  collect(msg, cb) {
    if (!['SUB', 'REQ'].includes(msg.op))
      return

    let subed = false
    const o = this.co[msg.op.toLowerCase()]

    if (!o[msg.channel]) {
      o[msg.channel] = { msg, cbs: [] }
    }
    else {
      subed = true
    }

    o[msg.channel].msg = msg

    if (cb) {
      const exist = o[msg.channel].cbs.findIndex(i => i === cb) > -1
      if (!exist) {
        o[msg.channel].cbs.push(cb)
      }
    }

    return subed
  }

  collectDel(msg, cb) {
    const o = this.co[msg.op.toLowerCase()]
    const items = o[msg.channel]

    if (cb) {
      const index = items.cbs.findIndex(i => i === cb)
      items.cbs.splice(index, 1)
    }
    else {
      delete o[msg.channel]
    }
  }

  send(msg, cb) {
    if (this.collect(msg, cb))
      return
    if (this.socket?.readyState === 1) {
      this.socket?.send(JSON.stringify(msg))
    }
  }

  reSend() {
    Object.values(this.co).forEach((o) => {
      Object.values(o).forEach(({ msg }) => this.socket?.send(JSON.stringify(msg)))
    })
  }

  subscribe(channel) {
    this.send({ op: 'SUB', channel: [channel] })
  }

  unsubscribe(channel, cb) {
    this.socket.send(JSON.stringify({
      op: 'UNSUB',
      channel: [channel],
    }))

    this.collectDel({
      op: 'SUB',
      channel: [channel],
    }, cb)
  }

  pong() {
    const msg = JSON.stringify({
      op: 'pong',
      epochMillis: new Date().getTime(),
    })
    this.socket.send(msg)
  }

  // 重置websocket心跳
  resetHeartbeat() {
    this.timerHb && clearTimeout(this.timerHb)
    this.timerRc && clearTimeout(this.timerRc)
    // 开启心跳链接, 5秒没有链接则主动pong，10秒没有消息则重连
    this.timerHb = setTimeout(() => {
      this.pong()
    }, 5000)
    // 保持心跳，10秒没有心跳则重连
    this.timerRc = setTimeout(() => {
      this.timerHb && clearTimeout(this.timerHb) // 关闭pong
      // 判断websocket当前状态
      this.connect()
    }, 10000)
  }

  auth() {
    const token = import.meta.client ? (localStorage.token || cookie.get('exch-token')) : ''
    if (token) {
      this.send({ op: 'login', auth: { token }, params: {} })
    }
  }
}

export default new WS()

export class InnerWs extends WS {
  constructor(url) {
    super()
    this.connect(url)
  }

  connect(url = this.wsUrl) {
    if (this.socket && this.socket.readyState !== 3) {
      this.socket.close()
      this.socket = null
    }
    this.socket = new InnerWebSocket(url)
    this.socket.on('open', this.onopen.bind(this))
    this.socket.on('message', this.onmessage.bind(this))
    this.socket.on('error', this.onerror.bind(this))
    this.socket.on('close', this.onclose.bind(this))
  }

  onmessage(msg) {
    try {
      const decoder = new TextDecoder('utf-8')
      const d = JSON.parse(decoder.decode(msg))
      formatter[d.T]?.(d)

      if (d.T === 'req') {
        const item = this.co.req[d.channel]
        item?.cbs?.forEach(i => i(d))
        delete this.co.req[d.channel]
      }
      else if (d.T === 'ping') {
        this.pong()
      }
      else {
        this.co.sub[d.channel]?.cbs?.forEach(i => i(d))
      }

      // 每次收到消息后，重置心跳
      this.resetHeartbeat()
    }
    catch (e) {
      console.log('ws onmessage', e)
    }
  }
}
